import { useEffect, useState, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Checkbox,
  Paper,
  FormControlLabel,
  Typography,
} from '@mui/material';
import { LoadingScreen } from 'src/components/loading-screen';
import { useNavSettings } from 'src/contexts/nav-bar/nav-setting-context';
import { getRequest, postRequest } from 'src/services';
import { endpoints } from 'src/utils/axios';
import { enqueueSnackbar } from 'notistack';

const Settings = () => {
  const { selectedLabels, toggleLabel, loading, setSelectedLabels, setLoading } = useNavSettings();
  const [settingsData, setSettingsData] = useState({});
  const isInitialMount = useRef(true);
  const isUserAction = useRef(false);

  const getSettings = async () => {
    setLoading(true);
    try {
      const data = await getRequest(endpoints.accounting.settings.get_list);
      if (data) {
        setSettingsData(data);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getSettings();
  }, []);

  useEffect(() => {
    if (Object.keys(settingsData).length > 0) {
      isUserAction.current = false;
      setSelectedLabels({
        Budget: false,
        Proposals: settingsData?.proposal === 1,
        Contracts: settingsData?.contracts === 1,
        'Change Orders': settingsData?.change_orders === 1,
        'Accounts Receivable': false,
        'Customer Invoices': settingsData?.customers_invoices === 1,
        'Payment Requisitions': settingsData?.payment_requisitions === 1,
        'Accounts Payable': false,
        'Purchase Orders': settingsData?.purchase_orders === 1,
        'Subcontractors Amount': settingsData?.subcontractor_amount === 1,
        'Work Orders': settingsData?.work_order === 1,
        'Vendor Invoices': false,
        Payroll: false,
        'Weekly Payroll Time Sheets': settingsData?.weekly_payroll_time_sheet === 1,
        'Run Payroll': settingsData?.run_payroll === 1,
        'Generate Payroll': settingsData?.generate_payroll === 1,
        'Payroll Taxes': settingsData?.payroll_taxes === 1,
        'Certified Payroll': settingsData?.certified_payroll === 1,
        Taxes: false,
        'Sales Taxes': settingsData?.sales_taxes === 1,
        'Business Taxes': settingsData?.business_taxes === 1,
        'Misc. Taxes': settingsData?.misc_taxes === 1,
      });
    }
  }, [settingsData]);

  const updateSettings = async () => {
    const apiData = {
      proposal: selectedLabels?.Proposals ? 1 : 0,
      contracts: selectedLabels?.Contracts ? 1 : 0,
      change_orders: selectedLabels?.['Change Orders'] ? 1 : 0,
      customers_invoices: selectedLabels?.['Customers Invoices'] ? 1 : 0,
      payment_requisitions: selectedLabels?.['Payment Requisitions'] ? 1 : 0,
      purchase_orders: selectedLabels?.['Purchase Orders'] ? 1 : 0,
      subcontractor_amount: selectedLabels?.['Subcontractor Amount'] ? 1 : 0,
      work_order: selectedLabels?.['Work Orders'] ? 1 : 0,
      weekly_payroll_time_sheet: selectedLabels?.['Weekly Payroll Time Sheets'] ? 1 : 0,
      run_payroll: selectedLabels?.['Run Payroll'] ? 1 : 0,
      payroll_taxes: selectedLabels?.['Payroll Taxes'] ? 1 : 0,
      generate_payroll: selectedLabels?.['Generate Payroll'] ? 1 : 0,
      certified_payroll: selectedLabels?.['Certified Payroll'] ? 1 : 0,
      sales_taxes: selectedLabels?.['Sales Taxes'] ? 1 : 0,
      business_taxes: selectedLabels?.['Business Taxes'] ? 1 : 0,
      misc_taxes: selectedLabels?.['Misc. Taxes'] ? 1 : 0,
    };
    try {
      setLoading(true);
      const response = await postRequest(endpoints.accounting.settings.post_list, apiData);
      if (response?.metadata?.success) {
        enqueueSnackbar(response?.metadata?.message, { variant: 'success' });
      }
    } catch (error) {
      console.error('Error updating settings:', error);
      enqueueSnackbar('Failed to update settings', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleChildCheckboxChange = (childName) => (event) => {
    isUserAction.current = true;
    toggleLabel(childName, labels);
  };

  useEffect(() => {
    if (!isInitialMount.current && isUserAction.current && Object.keys(selectedLabels).length > 0) {
      updateSettings();
    }
    isInitialMount.current = false;
  }, [selectedLabels]);

  const labels = [
    {
      id: 1,
      name: 'Budget',
      children: ['Proposals', 'Contracts', 'Change Orders'],
    },
    {
      id: 2,
      name: 'Accounts Receivable',
      children: ['Customers Invoices', 'Payment Requisitions'],
    },
    {
      id: 3,
      name: 'Accounts Payable',
      children: ['Purchase Orders', 'Subcontractor Amount', 'Work Orders'],
    },
    {
      id: 4,
      name: 'Payroll',
      children: ['Weekly Payroll Time Sheets', 'Generate Payroll', 'Run Payroll', 'Certified Payroll'],
    },
    {
      id: 5,
      name: 'Taxes',
      children: ['Sales Taxes', 'Payroll Taxes', 'Business Taxes', 'Misc. Taxes'],
    },
  ];

  return (
    <>
      <Card sx={{ height: '100%', marginTop: '10px', overflow: 'hidden' }}>
        <CardContent>
          {loading ? (
            <Box height={700}>
              <LoadingScreen />
            </Box>
          ) : (
            <>
             <Box display="flex" justifyContent="start" width="100%">
            <Card
              sx={{
                backgroundColor: '#FFFBEA',
                borderLeft: '4px solid #FFD700',
                borderRadius: '8px',
                boxShadow: 'none',
                padding: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                mt: 2,
                mb: 2,
              }}
            >
              {/* <InfoIcon sx={{ color: "#FFD700", fontSize: 22 }} /> */}
              <CardContent sx={{ padding: '0px !important' }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: '14px',
                    color: '#444',
                    lineHeight: 1.5,
                  }}
                >
                  <strong>Note:</strong> To hide any field from the sidebar list, simply check the corresponding checkbox and save your settings.
                </Typography>
              </CardContent>
            </Card>
          </Box>
            <Card
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
                gap: '20px',
                marginTop: '20px',
                padding: '30px',
                borderRadius: '1px',
              }}
            >
              {labels.map((label, index) => (
                <Paper
                  key={label.id}
                  style={{
                    padding: '15px 15px',
                    borderRadius: '4px',
                    boxShadow: 'rgba(149, 157, 165, 0.2) 0px 8px 24px',
                    marginTop: '10px',
                    border: '1px solid #CED4DA',
                  }}
                >
                  <div key={index}>
                    <Typography variant="h6">{label.name}</Typography>
                    {label.children.map((child, childIndex) => (
                      <Paper
                        key={childIndex}
                        style={{
                          padding: '5px 15px',
                          borderRadius: '4px',
                          boxShadow: 'rgba(149, 157, 165, 0.2) 0px 8px 24px',
                          marginTop: '10px',
                          border: '1px solid #CED4DA',
                        }}
                      >
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={selectedLabels[child] || false}
                              onChange={handleChildCheckboxChange(child)}
                            />
                          }
                          label={child}
                          disabled={
                            !selectedLabels[child] && 
                            label.children.filter(c => selectedLabels[c]).length === label.children.length - 1
                          }
                        />
                      </Paper>
                    ))}
                  </div>
                </Paper>
              ))}
            </Card>
            </>
          )}
        </CardContent>
      </Card>
    </>
  );
};

export default Settings;